import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Wrench, CheckCircle2, LogIn, UserPlus, ArrowLeft, Shield, Star, Award,
  ClipboardList, MapPin, Phone, Mail, AlertTriangle, Eye, EyeOff,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  registerInstaladorComelit, loginInstaladorComelit, getInstalaldoresComelit,
  getLeads,
  ROUTE_PATHS, type InstaladorComelit, type Lead,
} from '@/lib/index';
import { queueEmail } from '@/lib/airtable';

// ─── Schemas ──────────────────────────────────────────────────────────────────
const registerSchema = z.object({
  nome: z.string().min(2, 'Nome obrigatório'),
  email: z.string().email('Email inválido'),
  password: z.string().min(6, 'Mínimo 6 caracteres'),
  telefone: z.string().min(9, 'Telefone inválido'),
  nif: z.string().min(9, 'NIF inválido').max(9, 'NIF deve ter 9 dígitos'),
  zona: z.string().min(2, 'Zona obrigatória'),
  morada: z.string().min(5, 'Morada obrigatória'),
  temSeguro: z.boolean(),
  numeroClienteContera: z.string().optional(),
});

const loginSchema = z.object({
  email: z.string().email('Email inválido'),
  password: z.string().min(1, 'Palavra-passe obrigatória'),
});

type RegisterData = z.infer<typeof registerSchema>;
type LoginData = z.infer<typeof loginSchema>;

const zonas = ['Lisboa', 'Porto', 'Coimbra', 'Braga', 'Setúbal', 'Faro / Algarve', 'Aveiro', 'Évora', 'Viseu', 'Outra'];

const benefits = [
  { icon: ClipboardList, label: 'Pedidos de instalação na sua zona' },
  { icon: Award, label: 'Certificação Comelit reconhecida' },
  { icon: Shield, label: 'Supervisão e apoio da AT CONTERA' },
  { icon: Shield, label: 'Garantia de 5 anos AT CONTERA no final de cada obra' },
];

// ─── Sub-views ────────────────────────────────────────────────────────────────
type View = 'landing' | 'register' | 'login' | 'dashboard';

export default function InstallerPage() {
  const [view, setView] = useState<View>('landing');
  const [installer, setInstaller] = useState<InstaladorComelit | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [loginError, setLoginError] = useState('');
  const registeringRef = useRef(false);
  const [showPw, setShowPw] = useState(false);

  // Restaurar sessão ao carregar
  useEffect(() => {
    const sid = localStorage.getItem('installerSession');
    if (sid) {
      getInstalaldoresComelit().then(list => {
        const found = list.find(i => i.id === sid);
        if (found) { setInstaller(found); setView('dashboard'); }
        else localStorage.removeItem('installerSession');
      });
    }
  }, []);

  // ── Register ────────────────────────────────────────────────────────────────
  const regForm = useForm<RegisterData>({
    resolver: zodResolver(registerSchema),
    defaultValues: { temSeguro: false },
  });

  const onRegister = async (data: RegisterData) => {
    if (registeringRef.current) return;
    registeringRef.current = true;
    setRegisterError('');
    try {
      await registerInstaladorComelit({
        nome: data.nome,
        email: data.email,
        password: data.password,
        telefone: data.telefone,
        nif: data.nif,
        zona: data.zona,
        morada: data.morada,
        temSeguro: data.temSeguro,
        numeroClienteContera: data.numeroClienteContera ?? '',
      });

      // Emails em paralelo — erros de email não bloqueiam o registo
      await Promise.allSettled([
        queueEmail({
          email: 'comercial@contera.pt',
          nome: 'AT CONTERA',
          assunto: `[AT CONTERA] Novo registo de instalador — ${data.nome}`,
          mensagem: `Novo pedido de registo como Instalador Certificado Comelit:\n\nNome: ${data.nome}\nEmail: ${data.email}\nTelefone: ${data.telefone}\nNIF: ${data.nif}\nZona: ${data.zona}\nMorada: ${data.morada}\nNº Cliente AT CONTERA: ${data.numeroClienteContera || '—'}\nSeguro: ${data.temSeguro ? 'Sim' : 'Não'}\n\nAceda ao dashboard para aprovar ou rejeitar:\n${window.location.origin}/#/dashboard`,
        }),
        queueEmail({
          email: data.email,
          nome: data.nome,
          assunto: 'AT CONTERA — Registo recebido, em análise',
          mensagem: `Olá ${data.nome},\n\nO seu pedido de registo como Instalador Certificado Comelit AT CONTERA foi recebido com sucesso e encontra-se em análise.\n\nEm breve a nossa equipa irá verificar os seus dados e receberá um email de confirmação.\n\nDados submetidos:\nNome: ${data.nome}\nZona: ${data.zona}\nNIF: ${data.nif}\n\nObrigado por se juntar à rede AT CONTERA.\n\nAT CONTERA | comercial@contera.pt`,
        }),
      ]);

      setRegisterSuccess(true);
    } catch (e) {
      console.error('Erro no registo:', e);
      setRegisterError('Ocorreu um erro ao guardar o registo. Verifique os dados e tente novamente.');
    } finally {
      registeringRef.current = false;
    }
  };

  // ── Login ───────────────────────────────────────────────────────────────────
  const loginForm = useForm<LoginData>({ resolver: zodResolver(loginSchema) });

  const onLogin = async (data: LoginData) => {
    const found = await loginInstaladorComelit(data.email, data.password);
    if (!found) { setLoginError('Email ou palavra-passe incorretos.'); return; }
    setInstaller(found);
    localStorage.setItem('installerSession', found.id);
    setView('dashboard');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/80 sticky top-0 z-10 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={ROUTE_PATHS.HOME} className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <span className="text-primary-foreground font-bold text-sm">C</span>
            </div>
            <div>
              <span className="font-bold text-foreground text-base tracking-tight">AT CONTERA</span>
              <span className="text-muted-foreground text-xs block leading-none">Área do Instalador</span>
            </div>
          </Link>
          <Button asChild variant="ghost" size="sm">
            <Link to={ROUTE_PATHS.HOME}><ArrowLeft size={14} className="mr-1" />Início</Link>
          </Button>
        </div>
      </header>

      <div className="max-w-xl mx-auto px-4 py-10">
        <AnimatePresence mode="wait">

          {/* LANDING */}
          {view === 'landing' && (
            <motion.div key="landing" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-10">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                  <Wrench size={32} className="text-primary" />
                </div>
                <div className="inline-flex items-center gap-2 bg-primary/10 text-primary text-xs font-semibold px-3 py-1.5 rounded-full mb-4">
                  <Star size={12} />Instalador Certificado Comelit
                </div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Faça parte da nossa rede</h1>
                <p className="text-muted-foreground text-sm leading-relaxed max-w-sm mx-auto">
                  Junte-se à rede de Instaladores Certificados Comelit da AT CONTERA. Receba pedidos de orçamento da sua zona e beneficie do apoio e reconhecimento de 66 anos de experiência.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-8">
                {benefits.map((b) => (
                  <div key={b.label} className="bg-card border border-border rounded-xl p-3 flex items-center gap-2">
                    <b.icon size={16} className="text-primary shrink-0" />
                    <p className="text-xs font-medium text-foreground">{b.label}</p>
                  </div>
                ))}
              </div>

              {/* ── MANUAL DA PLATAFORMA ── */}
              <div className="mb-8 rounded-2xl border border-border overflow-hidden">
                {/* Cabeçalho do manual */}
                <div className="bg-primary px-5 py-4">
                  <p className="text-xs font-semibold text-primary-foreground/70 uppercase tracking-wider mb-0.5">Guia do Instalador</p>
                  <h2 className="text-base font-bold text-white">Como funciona a plataforma AT CONTERA</h2>
                </div>

                <div className="bg-card divide-y divide-border">

                  {/* Passo 1 */}
                  <div className="p-4 flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">1</div>
                    <div>
                      <p className="font-semibold text-sm text-foreground mb-1">Registo e aprovação</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Preenche o formulário de registo com os seus dados, NIF, seguro, zona de atuação e número de cliente AT CONTERA. A equipa AT CONTERA analisa o seu perfil e aprova-o como <strong>Instalador Certificado Comelit</strong>. Receberá uma notificação por email quando a conta for aprovada.
                      </p>
                    </div>
                  </div>

                  {/* Passo 2 */}
                  <div className="p-4 flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">2</div>
                    <div>
                      <p className="font-semibold text-sm text-foreground mb-1">Receção de pedidos</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Quando um cliente solicita instalação na sua zona, a AT CONTERA atribui-lhe o pedido e notifica-o por email com os dados do cliente. Deve contactar o cliente directamente para agendar a visita técnica.
                      </p>
                    </div>
                  </div>

                  {/* Passo 3 */}
                  <div className="p-4 flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">3</div>
                    <div>
                      <p className="font-semibold text-sm text-foreground mb-1">Visita técnica e instalação</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Contacta o cliente para agendar a visita técnica. Após a visita, acorda as condições com a AT CONTERA e procede à instalação com o material fornecido pela AT CONTERA.
                      </p>
                    </div>
                  </div>

                  {/* Passo 4 */}
                  <div className="p-4 flex gap-4">
                    <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-sm shrink-0 mt-0.5">4</div>
                    <div>
                      <p className="font-semibold text-sm text-foreground mb-1">Conclusão da obra</p>
                      <p className="text-xs text-muted-foreground leading-relaxed">
                        Conclui a obra e notifica a AT CONTERA. A AT CONTERA emite a Garantia Oficial de 5 Anos ao cliente. Este serviço é 100% gratuito para si.
                      </p>
                    </div>
                  </div>

                  {/* Bloco pontos de levantamento */}
                  <div className="p-4 border-t border-border">
                    <div className="flex gap-3">
                      <div className="w-8 h-8 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center shrink-0 mt-0.5">
                        <MapPin size={15} />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm text-foreground mb-1">📦 Pontos de levantamento de material</p>
                        <p className="text-xs text-muted-foreground mb-3">Levante o material Comelit para a sua obra nestes parceiros AT CONTERA em Lisboa:</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="border border-border rounded-xl p-3 bg-muted/30">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-base">🏪</span>
                              <span className="font-bold text-sm text-foreground">Carceluz</span>
                              <span className="ml-auto text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">Lisboa</span>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">Parceiro AT CONTERA · Material Comelit disponível para levantamento</p>
                          </div>
                          <div className="border border-border rounded-xl p-3 bg-muted/30">
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-base">🏪</span>
                              <span className="font-bold text-sm text-foreground">Joteilux</span>
                              <span className="ml-auto text-[10px] font-semibold text-blue-600 bg-blue-50 border border-blue-100 rounded-full px-2 py-0.5">Lisboa</span>
                            </div>
                            <p className="text-xs text-muted-foreground leading-relaxed">Parceiro AT CONTERA · Material Comelit disponível para levantamento</p>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-3 italic">ℹ️ A AT CONTERA confirma o ponto de levantamento e os detalhes do material a recolher.</p>
                      </div>
                    </div>
                  </div>

                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button className="flex-1" size="lg" onClick={() => setView('register')}>
                  <UserPlus size={16} className="mr-2" />Registar-me
                </Button>
                <Button variant="outline" className="flex-1" size="lg" onClick={() => setView('login')}>
                  <LogIn size={16} className="mr-2" />Já tenho conta
                </Button>
              </div>
            </motion.div>
          )}

          {/* REGISTER */}
          {view === 'register' && !registerSuccess && (
            <motion.div key="register" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <button onClick={() => setView('landing')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
                <ArrowLeft size={14} />Voltar
              </button>
              <h2 className="text-xl font-bold text-foreground mb-1">Registo de Instalador</h2>
              <p className="text-sm text-muted-foreground mb-6">Preencha os dados para ser avaliado pela equipa AT CONTERA.</p>

              <form onSubmit={regForm.handleSubmit(onRegister)} className="space-y-5 bg-card border border-border rounded-2xl p-6">
                {/* Pessoais */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1">
                    <Phone size={12} />Dados Pessoais
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>Nome completo *</Label>
                      <Input placeholder="João Silva" {...regForm.register('nome')} />
                      {regForm.formState.errors.nome && <p className="text-destructive text-xs">{regForm.formState.errors.nome.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label>Email *</Label>
                      <Input type="email" placeholder="joao@email.com" {...regForm.register('email')} />
                      {regForm.formState.errors.email && <p className="text-destructive text-xs">{regForm.formState.errors.email.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label>Telefone *</Label>
                      <Input placeholder="912 345 678" {...regForm.register('telefone')} />
                      {regForm.formState.errors.telefone && <p className="text-destructive text-xs">{regForm.formState.errors.telefone.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label>NIF *</Label>
                      <Input placeholder="123456789" maxLength={9} {...regForm.register('nif')} />
                      {regForm.formState.errors.nif && <p className="text-destructive text-xs">{regForm.formState.errors.nif.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label>Palavra-passe *</Label>
                      <div className="relative">
                        <Input type={showPw ? 'text' : 'password'} placeholder="Mínimo 6 caracteres" {...regForm.register('password')} />
                        <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                          {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                      </div>
                      {regForm.formState.errors.password && <p className="text-destructive text-xs">{regForm.formState.errors.password.message}</p>}
                    </div>
                  </div>
                </div>

                {/* Profissional */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3 flex items-center gap-1">
                    <MapPin size={12} />Dados Profissionais
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-1.5 sm:col-span-2">
                      <Label>Morada *</Label>
                      <Input placeholder="Rua, nº, localidade" {...regForm.register('morada')} />
                      {regForm.formState.errors.morada && <p className="text-destructive text-xs">{regForm.formState.errors.morada.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label>Zona de atuação *</Label>
                      <select {...regForm.register('zona')} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                        <option value="">Selecionar zona</option>
                        {zonas.map((z) => <option key={z} value={z}>{z}</option>)}
                      </select>
                      {regForm.formState.errors.zona && <p className="text-destructive text-xs">{regForm.formState.errors.zona.message}</p>}
                    </div>
                    <div className="space-y-1.5">
                      <Label>Nº de cliente AT CONTERA</Label>
                      <Input placeholder="(se já for cliente)" {...regForm.register('numeroClienteContera')} />
                    </div>
                    <div className="space-y-1.5 sm:col-span-2">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input type="checkbox" {...regForm.register('temSeguro')} className="w-4 h-4 accent-primary" />
                        <span className="text-sm text-foreground">Possuo seguro de responsabilidade civil profissional</span>
                      </label>
                    </div>
                  </div>
                </div>

                {registerError && (
                  <p className="text-destructive text-sm flex items-center gap-1.5 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                    <AlertTriangle size={14} />{registerError}
                  </p>
                )}
                <Button type="submit" className="w-full" size="lg" disabled={regForm.formState.isSubmitting}>
                  <UserPlus size={16} className="mr-2" />{regForm.formState.isSubmitting ? 'A submeter...' : 'Submeter Registo'}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Após submissão, a equipa AT CONTERA irá analisar o seu perfil e dar-lhe retorno por email.
                </p>
              </form>
            </motion.div>
          )}

          {/* REGISTER SUCCESS */}
          {view === 'register' && registerSuccess && (
            <motion.div key="reg-success" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-10">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 size={40} className="text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-foreground mb-2">Registo Submetido!</h2>
              <p className="text-muted-foreground text-sm mb-6">
                O seu pedido foi recebido. A equipa AT CONTERA irá analisar o seu perfil e entrar em contacto por email em breve.
              </p>
              <div className="bg-muted rounded-xl p-4 text-left text-sm mb-6 space-y-1.5">
                <p className="flex items-center gap-2"><CheckCircle2 size={14} className="text-green-600" />Perfil em análise</p>
                <p className="flex items-center gap-2"><CheckCircle2 size={14} className="text-green-600" />Certificação Comelit verificada</p>
                <p className="flex items-center gap-2"><CheckCircle2 size={14} className="text-green-600" />Acesso ao dashboard após aprovação</p>
              </div>
              <Button onClick={() => { setView('login'); setRegisterSuccess(false); }} className="w-full">
                <LogIn size={16} className="mr-2" />Entrar na minha conta
              </Button>
            </motion.div>
          )}

          {/* LOGIN */}
          {view === 'login' && (
            <motion.div key="login" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>
              <button onClick={() => setView('landing')} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-6">
                <ArrowLeft size={14} />Voltar
              </button>
              <h2 className="text-xl font-bold text-foreground mb-1">Aceder à minha conta</h2>
              <p className="text-sm text-muted-foreground mb-6">Área exclusiva para instaladores certificados.</p>

              <form onSubmit={loginForm.handleSubmit(onLogin)} className="space-y-4 bg-card border border-border rounded-2xl p-6">
                <div className="space-y-1.5">
                  <Label>Email</Label>
                  <Input type="email" placeholder="joao@email.com" {...loginForm.register('email')} />
                  {loginForm.formState.errors.email && <p className="text-destructive text-xs">{loginForm.formState.errors.email.message}</p>}
                </div>
                <div className="space-y-1.5">
                  <Label>Palavra-passe</Label>
                  <div className="relative">
                    <Input type={showPw ? 'text' : 'password'} placeholder="••••••" {...loginForm.register('password')} />
                    <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                      {showPw ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  {loginForm.formState.errors.password && <p className="text-destructive text-xs">{loginForm.formState.errors.password.message}</p>}
                </div>
                {loginError && (
                  <p className="text-destructive text-xs flex items-center gap-1">
                    <AlertTriangle size={12} />{loginError}
                  </p>
                )}
                <Button type="submit" className="w-full" size="lg">
                  <LogIn size={16} className="mr-2" />Entrar
                </Button>
                <p className="text-xs text-center text-muted-foreground">
                  Ainda não tem conta?{' '}
                  <button type="button" className="text-primary underline" onClick={() => setView('register')}>Registar-me</button>
                </p>
              </form>
            </motion.div>
          )}

          {/* DASHBOARD */}
          {view === 'dashboard' && installer && (
            <InstallerDashboard installer={installer} onLogout={() => { setInstaller(null); localStorage.removeItem('installerSession'); setView('landing'); }} />
          )}

        </AnimatePresence>
      </div>

      <footer className="border-t border-border mt-8 py-6 text-center text-xs text-muted-foreground">
        <p>© {new Date().getFullYear()} AT CONTERA · Instaladores Certificados Comelit · 66 anos de experiência</p>
      </footer>
    </div>
  );
}

// ─── Installer Dashboard ───────────────────────────────────────────────────────
function InstallerDashboard({ installer, onLogout }: { installer: InstaladorComelit; onLogout: () => void }) {
  const [leads, setLeads] = useState<Lead[]>([]);

  useEffect(() => {
    getLeads().then(all => {
      setLeads(all.filter(l =>
        l.instaladorId === installer.id ||
        l.instaladorIds?.includes(installer.id)
      ));
    });
  }, [installer]);

  return (
    <motion.div key="dashboard" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-5">
      {/* Welcome */}
      <div className="bg-card border border-border rounded-2xl p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Bem-vindo</p>
            <p className="font-bold text-lg text-foreground">{installer.nome}</p>
            <div className="flex items-center gap-2 mt-1 flex-wrap">
              <Badge className={`text-xs border ${installer.status === 'aprovado' ? 'bg-green-100 text-green-700 border-green-200' : installer.status === 'pendente' ? 'bg-yellow-100 text-yellow-700 border-yellow-200' : 'bg-red-100 text-red-700 border-red-200'}`}>
                {installer.status === 'aprovado' ? '✓ Aprovado' : installer.status === 'pendente' ? '⏳ Em análise' : '✗ Rejeitado'}
              </Badge>
              <span className="text-xs text-muted-foreground flex items-center gap-1"><MapPin size={11} />{installer.zona}</span>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={onLogout}>Sair</Button>
        </div>
      </div>

      {installer.status !== 'aprovado' ? (
        <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-5 text-center">
          <AlertTriangle size={24} className="text-yellow-600 mx-auto mb-2" />
          <p className="font-semibold text-yellow-800 text-sm">Conta em análise</p>
          <p className="text-yellow-700 text-xs mt-1">A equipa AT CONTERA está a verificar o seu perfil. Receberá um email quando a sua conta for aprovada.</p>
        </div>
      ) : (
        <>
          {/* Info: como recebe pedidos */}
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
            <div className="flex gap-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center shrink-0">
                <Mail size={16} className="text-blue-600" />
              </div>
              <div>
                <p className="font-semibold text-sm text-blue-900 mb-1">Como recebe pedidos de instalação</p>
                <p className="text-xs text-blue-700 leading-relaxed">
                  Quando a AT CONTERA lhe atribuir um pedido de instalação, receberá um email com os dados do cliente. Contacte-o directamente para agendar a visita técnica.
                </p>
              </div>
            </div>
          </div>

          {/* Leads atribuídos */}
          <div>
            <h3 className="font-semibold text-foreground mb-3 flex items-center gap-2">
              <ClipboardList size={16} className="text-primary" />
              Instalações atribuídas
              <Badge className="text-xs bg-primary/10 text-primary border-0 ml-1">{leads.length}</Badge>
            </h3>
            {leads.length === 0 ? (
              <div className="bg-muted/40 border border-dashed border-border rounded-2xl p-8 text-center">
                <ClipboardList size={24} className="text-muted-foreground mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">Ainda não lhe foram atribuídas instalações.</p>
                <p className="text-xs text-muted-foreground mt-1">Quando a AT CONTERA lhe atribuir um pedido, receberá um email e aparecerá aqui.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {leads.map(l => (
                  <div key={l.id} className="bg-card border border-border rounded-xl p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div>
                        <p className="font-mono text-xs text-muted-foreground">CTR-{l.id.slice(0,8).toUpperCase()}</p>
                        <p className="font-semibold text-sm text-foreground mt-0.5">{l.nome}</p>
                      </div>
                      <Badge className="text-xs bg-green-100 text-green-700 border-green-200 border">Atribuído</Badge>
                    </div>
                    <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><MapPin size={11} />{l.localidade}</span>
                      <span className="flex items-center gap-1"><Phone size={11} />{l.telefone}</span>
                      <span className="flex items-center gap-1"><Mail size={11} />{l.email}</span>
                    </div>
                    {l.dataVisita && (
                      <p className="text-xs text-primary font-medium mt-2">📅 Visita: {l.dataVisita.split('-').reverse().join('/')}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Garantia AT CONTERA */}
          <div className="rounded-2xl border border-green-200 overflow-hidden">
            <div className="bg-green-50 px-4 py-3 flex gap-3">
              <div className="w-8 h-8 rounded-full bg-green-100 text-green-700 flex items-center justify-center shrink-0 mt-0.5">
                <Shield size={15} />
              </div>
              <div>
                <p className="font-semibold text-sm text-green-900 mb-1">🛡️ Garantia AT CONTERA — 5 Anos</p>
                <ul className="text-xs text-green-800 space-y-1 leading-relaxed">
                  <li>• No final de cada obra, a AT CONTERA emite a <strong>Garantia Oficial de 5 Anos</strong> ao cliente</li>
                  <li>• Esta garantia é dada por uma empresa com <strong>mais de 6 décadas no mercado</strong></li>
                  <li>• É a maior segurança que pode oferecer ao seu cliente</li>
                  <li>• Guarde sempre a referência da obra para activar a garantia</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Condições Comerciais */}
      <div className="rounded-2xl border border-border overflow-hidden">
        <div className="bg-primary px-5 py-4">
          <p className="text-xs font-semibold text-primary-foreground/70 uppercase tracking-wider mb-0.5">Condições Comerciais</p>
          <h3 className="text-base font-bold text-white">Tabela de Mão de Obra & Modelo de Negócio</h3>
        </div>
        <div className="bg-card p-4 grid grid-cols-1 sm:grid-cols-2 gap-3 border-b border-border">
          {[
            { emoji: '🛡️', title: 'Garantia de 5 Anos', desc: 'A AT CONTERA emite uma Garantia Oficial de 5 Anos ao cliente no final de cada obra' },
            { emoji: '🏆', title: '66 Anos de Experiência', desc: 'A maior segurança que pode oferecer ao cliente — respaldada por mais de 6 décadas no mercado' },
            { emoji: '🔧', title: 'Material Comelit', desc: 'A AT CONTERA fornece o material Comelit certificado para cada obra' },
            { emoji: '🎁', title: 'Serviço 100% Gratuito', desc: 'O serviço da AT CONTERA e da plataforma é totalmente gratuito para o instalador' },
          ].map(item => (
            <div key={item.title} className="flex gap-3 p-3 rounded-xl bg-muted/40 border border-border">
              <span className="text-2xl shrink-0">{item.emoji}</span>
              <div>
                <p className="font-semibold text-sm text-foreground">{item.title}</p>
                <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="bg-card p-4">
          <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">Tabela de Ligações Vídeo/Áudio — Mão de Obra (valor indicativo)</p>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-primary text-primary-foreground">
                  <th className="text-left px-4 py-2.5 font-semibold text-xs">Nº Inquilinos</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-xs">📹 Vídeo</th>
                  <th className="text-right px-4 py-2.5 font-semibold text-xs">🔊 Áudio</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[
                  { range: 'Até 10', video: '430,00 €', audio: '380,00 €' },
                  { range: '11 a 15', video: '600,00 €', audio: '550,00 €' },
                  { range: '16 a 20', video: '650,00 €', audio: '600,00 €' },
                  { range: '21 a 25', video: '760,00 €', audio: '730,00 €' },
                  { range: '26 a 30', video: '850,00 €', audio: '800,00 €' },
                  { range: '31 a 35', video: '980,00 €', audio: '900,00 €' },
                  { range: '36 a 40', video: '1 050,00 €', audio: '990,00 €' },
                  { range: '41 a 45', video: '1 200,00 €', audio: '1 100,00 €' },
                  { range: '46 a 50', video: '1 320,00 €', audio: '1 200,00 €' },
                  { range: 'Mais de 50', video: '+50 € / inquilino', audio: '+50 € / inquilino', note: true },
                ].map((row, i) => (
                  <tr key={row.range} className={i % 2 === 0 ? 'bg-muted/20' : 'bg-card'}>
                    <td className="px-4 py-2.5 font-medium text-foreground text-xs">{row.range}</td>
                    <td className={`px-4 py-2.5 text-right font-semibold text-xs ${row.note ? 'text-primary italic' : 'text-green-700'}`}>{row.video}</td>
                    <td className={`px-4 py-2.5 text-right font-semibold text-xs ${row.note ? 'text-primary italic' : 'text-blue-700'}`}>{row.audio}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground mt-2 italic">* Valores indicativos de mão de obra. O instalador recebe a totalidade deste valor.</p>
        </div>
      </div>
    </motion.div>
  );
}

